import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { sanityClient } from '@/lib/sanity/client';
import type { SITE_WIDE_SETTINGS_QUERYResult } from '@/types/sanity/content';
import { SITE_WIDE_SETTINGS_QUERY } from '@/lib/sanity/queries';


interface UseSanityQueryOptions<T> extends Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'> {
  query: string;
  params?: Record<string, unknown>;
  key: string | (string | number | undefined)[];
}

export function useSanityQuery<T = unknown>({
  query,
  params = {},
  key,
  ...options
}: UseSanityQueryOptions<T>) {
  const queryKey = Array.isArray(key) ? ['sanity', ...key] : ['sanity', key];
  const staleTime = import.meta.env.MODE === 'development' ? 0 : 1000 * 60 * 4; // 4 minutes

  return useQuery<T, Error>({
    queryKey,
    queryFn: async () => {
      const result = await sanityClient.fetch<T>(query, params);
      return result;
    },
    staleTime,
    meta: { persist: true }, // Enable localStorage caching
    ...options,
  });
}

// Convenience hook for fetching site-wide settings
export function useSiteWideSettings(
  options?: Omit<UseQueryOptions<SITE_WIDE_SETTINGS_QUERYResult, Error>, 'queryKey' | 'queryFn'>,
) {
  return useSanityQuery<SITE_WIDE_SETTINGS_QUERYResult>({
    query: SITE_WIDE_SETTINGS_QUERY,
    key: ['site-wide-settings'],
    ...options,
  });
}
